import { registerSW } from 'virtual:pwa-register';
import toast from 'react-hot-toast';

/**
 * Service-worker update + stale-chunk recovery for the SPA / installed PWA.
 *
 * Why this exists: the web shell is precached by workbox. After a deploy the
 * old worker keeps serving the old index.html, whose lazy-route chunks were
 * deleted from the server. A clinician with the app open (or installed as a
 * "desktop app") then hits "Failed to fetch dynamically imported module" the
 * next time they open Settings or To-dos, while a colleague who opened a
 * fresh tab is fine. Three pieces close that gap:
 *
 *  1. `registerServiceWorkerUpdates` — registers the worker in `prompt` mode,
 *     re-checks for a new worker every {@link UPDATE_CHECK_INTERVAL_MS} and
 *     whenever the window regains focus, and shows a persistent toast with an
 *     "Update" button when one is waiting. Applying the update reloads the page
 *     (workbox-window does that once the new worker takes control).
 *  2. `installPreloadErrorRecovery` — listens for Vite's `vite:preloadError`
 *     and reloads once so the browser picks up the current shell, instead of
 *     showing the error boundary. A timestamp in sessionStorage stops a reload
 *     loop if the chunk really is missing.
 *  3. `applyPendingUpdate` — lets other recovery paths (the API's
 *     CONSENT_VERSION_STALE answer in lib/api.ts) hand control to the waiting
 *     worker instead of calling `location.reload()`, which under a
 *     controlling prompt-mode worker just serves the same stale shell again.
 */

export const UPDATE_CHECK_INTERVAL_MS = 30 * 60_000;
const PRELOAD_RELOAD_KEY = 'clinikchat:preload-reload-at';
const PRELOAD_RELOAD_COOLDOWN_MS = 60_000;
const UPDATE_TOAST_ID = 'sw-update';
/** Set by LoginPage while a two-factor challenge is pending; blocks auto-apply (a reload would drop the pending token). */
export const LOGIN_IN_PROGRESS_KEY = 'clinikchat:login-in-progress';
const AUTO_APPLY_KEY = 'clinikchat:sw-auto-apply-at';
const AUTO_APPLY_COOLDOWN_MS = 60_000;
/** Signed-out pages: nothing worth protecting from a reload unless a field has been typed into. */
const GUEST_PATHS = new Set([
  '/login',
  '/register',
  '/join',
  '/forgot-password',
  '/reset-password',
]);

type MinimalStorage = Pick<Storage, 'getItem' | 'setItem'>;

function safeStorage(): MinimalStorage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Decide whether a preload failure should trigger a reload. Exported for
 * tests; pure apart from the storage read/write.
 */
export function shouldReloadForPreloadError(
  storage: MinimalStorage | null,
  now: number = Date.now(),
): boolean {
  // Without storage the once-per-cooldown guard cannot be kept: if the chunk is
  // still missing (a partial deploy, a server fault) every reloaded shell would
  // hit the same failure and reload again — an unbounded refresh loop. Refuse
  // and let the error surface (same rule as the stale-consent path).
  if (!storage) return false;
  const last = Number(storage.getItem(PRELOAD_RELOAD_KEY) ?? 0);
  if (Number.isFinite(last) && last > 0 && now - last < PRELOAD_RELOAD_COOLDOWN_MS) {
    return false;
  }
  storage.setItem(PRELOAD_RELOAD_KEY, String(now));
  return true;
}

/**
 * A lazy chunk failed to load. Under the prompt-mode service worker a plain
 * reload of a controlled page (installed PWA, or any tab the worker
 * controls) is answered from the CURRENT worker's precache — the same stale
 * shell that references the missing chunk — and the cooldown would then
 * suppress the next attempt, leaving the route broken. So when a worker
 * controls the page, activate a waiting one (which reloads onto the new
 * shell) or, if none is waiting yet, ask the user to apply the update.
 * A direct reload is only right when no worker is in play, where it does
 * fetch a fresh shell; that path keeps the once-per-cooldown guard.
 *
 * `preventDefault` has to run synchronously, so the choice is made from the
 * synchronous "is a worker controlling this page" signal.
 */
export function installPreloadErrorRecovery(win: Window = window): void {
  win.addEventListener('vite:preloadError', (event) => {
    const controlled = typeof navigator !== 'undefined' && !!navigator.serviceWorker?.controller;
    if (controlled) {
      event.preventDefault();
      void applyPendingUpdate()
        .then((applied) => {
          if (!applied) showUpdateRequiredToast();
        })
        .catch(() => showUpdateRequiredToast());
      return;
    }
    let reload = false;
    try {
      reload = shouldReloadForPreloadError(safeStorage());
    } catch {
      // Storage that exists but throws on use (policy, quota): the cooldown
      // guard cannot be kept, so never reload blindly; ask the user instead.
      event.preventDefault();
      showUpdateRequiredToast();
      return;
    }
    if (!reload) return;
    event.preventDefault();
    win.location.reload();
  });
}

/**
 * A waiting worker is applied without asking only when a reload cannot lose
 * anything: the visitor is on a signed-out page, has typed nothing into any
 * field, and we have not already auto-reloaded in the last minute (loop
 * guard). Everywhere else the "update available" toast stays the only path,
 * so a half-typed message is never dropped. Exported for tests.
 */
export function shouldAutoApplyUpdate(
  win: Pick<Window, 'location' | 'document'> = window,
  storage: MinimalStorage | null = safeStorage(),
  now: number = Date.now(),
): boolean {
  if (!storage) return false;
  if (!GUEST_PATHS.has(win.location.pathname.replace(/\/+$/, '') || '/')) return false;
  // A login that has started must never be reloaded away, including between
  // steps: the two-factor step replaces the (now empty) email/password fields
  // with an empty code input, and the pending token lives only in component state.
  if (storage.getItem(LOGIN_IN_PROGRESS_KEY)) return false;
  if (win.document.querySelector('#mfa-code, input[autocomplete="one-time-code"]')) return false;
  const fields = win.document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input, textarea',
  );
  for (const field of Array.from(fields)) {
    if (field.type === 'checkbox' || field.type === 'radio' || field.type === 'hidden') continue;
    if (field.value) return false;
  }
  const last = Number(storage.getItem(AUTO_APPLY_KEY) ?? 0);
  if (Number.isFinite(last) && last > 0 && now - last < AUTO_APPLY_COOLDOWN_MS) return false;
  storage.setItem(AUTO_APPLY_KEY, String(now));
  return true;
}

/** The slice of `document` the update poller needs; narrowed so tests can fake it. */
export interface VisibilityDocument {
  readonly visibilityState: DocumentVisibilityState;
  addEventListener(type: 'visibilitychange', listener: () => void): void;
}

export interface RegisterServiceWorkerOptions {
  /** Injected for tests; defaults to `window.setInterval`. */
  setInterval?: (handler: () => void, timeout: number) => unknown;
  /** Injected for tests; defaults to `document`. */
  doc?: VisibilityDocument;
}

/** The slice of `ServiceWorkerRegistration` the update paths use; narrowed so tests can fake it. */
export interface MinimalRegistration {
  readonly waiting: ServiceWorker | null;
  update(): Promise<unknown>;
}

interface WorkerState {
  updateSW: (reloadPage?: boolean) => Promise<void>;
  registration: MinimalRegistration | null;
}

// Set by registerServiceWorkerUpdates; null until the worker is registered
// (or forever, in browsers without service-worker support).
let workerState: WorkerState | null = null;

/** Test-only: forget the registration recorded by the last register call. */
export function resetServiceWorkerStateForTests(): void {
  workerState = null;
}

function showUpdateToast(message: string, onUpdate: () => void): void {
  toast(
    (t) => (
      <span className="flex items-center gap-3">
        <span>{message}</span>
        <button
          type="button"
          className="rounded-md bg-slate-900 px-2 py-1 text-xs font-semibold text-white dark:bg-white dark:text-slate-900"
          onClick={() => {
            toast.dismiss(t.id);
            onUpdate();
          }}
        >
          Update
        </button>
      </span>
    ),
    { id: UPDATE_TOAST_ID, duration: Infinity },
  );
}

/**
 * Hand control to a new service worker that is already installed and
 * waiting: SKIP_WAITING + a reload once it takes control (workbox-window).
 * Returns true when that happened. When no worker is waiting it asks the
 * registration to check for one and returns false — the caller decides
 * whether a plain reload is safe or whether to show the update prompt.
 */
export async function applyPendingUpdate(): Promise<boolean> {
  if (!workerState) return false;
  const { registration, updateSW } = workerState;
  if (registration?.waiting) {
    await updateSW(true);
    return true;
  }
  if (registration) {
    await registration.update().catch(() => {
      /* offline or transient — the next scheduled check will retry */
    });
    // A check may have just installed a worker that is now waiting.
    if (registration.waiting) {
      await updateSW(true);
      return true;
    }
  }
  return false;
}

const UPDATE_RELOAD_KEY = 'clinikchat:update-required-reload-at';
const UPDATE_RELOAD_COOLDOWN_MS = 60_000;

/**
 * What {@link updateAndReload} did, for the blocking screen to explain:
 *  - `reloading`      the page is being replaced (worker applied or cleared).
 *  - `offline`        nothing was touched: with no network a cleared shell could
 *                     not be re-fetched, so the installed app would not open.
 *  - `still-outdated` we already reloaded for this a minute ago and the server
 *                     still refuses this bundle; another reload cannot help.
 *  - `failed`         a service worker could not be removed, so a reload would
 *                     be answered by the same stale shell (navigateFallback).
 */
export type UpdateReloadOutcome = 'reloading' | 'offline' | 'still-outdated' | 'failed';

/** Delete workbox precaches. Independent of worker removal; never throws. */
async function clearPrecaches(): Promise<void> {
  try {
    if (typeof caches === 'undefined') return;
    const names = await caches.keys();
    await Promise.all(names.filter((n) => /precache/i.test(n)).map((n) => caches.delete(n)));
  } catch {
    /* best effort */
  }
}

/** Unregister every worker. True only when none is left that could serve the stale shell. */
async function unregisterAllWorkers(): Promise<boolean> {
  try {
    const sw = typeof navigator === 'undefined' ? undefined : navigator.serviceWorker;
    if (!sw?.getRegistrations) return true;
    const registrations = await sw.getRegistrations();
    const results = await Promise.all(
      registrations.map((r) =>
        r.unregister().then(
          (ok) => ok,
          () => false,
        ),
      ),
    );
    return results.every(Boolean);
  } catch {
    return false;
  }
}

/**
 * Explicit "Update" action from the blocking update-required screen (the server
 * answered 426 CLIENT_UPDATE_REQUIRED). Order:
 *  1. Offline: do nothing (see `offline`).
 *  2. Activate a waiting worker if there is one (reloads onto the new shell).
 *  3. Loop guard: reloaded for this within the last minute and still refused:
 *     report `still-outdated` instead of reloading again.
 *  4. Drop the workbox precache and unregister every worker, each independently.
 *     If a worker could not be removed, do NOT reload: the worker's
 *     navigateFallback would hand back the same stale index.html. Report `failed`.
 *  5. Record the attempt and reload.
 * Only ever called from a user click, never automatically, so it is exempt from
 * the login-in-progress / typed-input guards that protect `shouldAutoApplyUpdate`
 * (every API call is being rejected at that point, so a pending login cannot
 * finish anyway). Those guards and their storage keys are not touched here.
 */
export async function updateAndReload(): Promise<UpdateReloadOutcome> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  try {
    if (await applyPendingUpdate()) return 'reloading';
  } catch {
    /* fall through to the hard path */
  }
  const storage = safeStorage();
  try {
    const last = Number(storage?.getItem(UPDATE_RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && last > 0 && Date.now() - last < UPDATE_RELOAD_COOLDOWN_MS) {
      return 'still-outdated';
    }
  } catch {
    /* storage unusable: the click is manual, so a reload cannot loop */
  }
  const [workersCleared] = await Promise.all([unregisterAllWorkers(), clearPrecaches()]);
  if (!workersCleared) return 'failed';
  try {
    storage?.setItem(UPDATE_RELOAD_KEY, String(Date.now()));
  } catch {
    /* see above */
  }
  window.location.reload();
  return 'reloading';
}

/**
 * Prompt the user that the server has rejected this bundle as too old and a
 * newer version must be applied (lib/api.ts, CONSENT_VERSION_STALE). Clicking
 * Update applies a waiting worker if one exists and otherwise reloads: the
 * user asked for it explicitly, so a reload that turns out stale is a
 * one-off, not a loop.
 */
export function showUpdateRequiredToast(): void {
  showUpdateToast('A newer version of ClinikChat is required.', () => {
    void applyPendingUpdate().then((applied) => {
      if (!applied) window.location.reload();
    });
  });
}

/**
 * Register the PWA service worker and keep it current. Safe to call in
 * environments without service-worker support (returns without doing
 * anything).
 */
export function registerServiceWorkerUpdates(options: RegisterServiceWorkerOptions = {}): void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const schedule =
    options.setInterval ?? ((handler, timeout) => window.setInterval(handler, timeout));
  const doc: VisibilityDocument = options.doc ?? document;

  let pendingRegistration: MinimalRegistration | null = null;
  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh() {
      // Deferred a tick: registerSW may call this before it has returned
      // `updateSW` (tests do), so the binding must not be read synchronously.
      void Promise.resolve().then(() => {
        let auto = false;
        try {
          auto = shouldAutoApplyUpdate();
        } catch {
          auto = false;
        }
        if (auto) {
          void updateSW(true);
          return;
        }
        showUpdateToast('A new version of ClinikChat is ready.', () => void updateSW(true));
      });
    },
    onRegisteredSW(_url, registration) {
      // registerSW may invoke this synchronously (tests do), before the
      // `updateSW` binding below exists — record the registration only and
      // let the assignment after registerSW() attach the updater.
      pendingRegistration = registration ?? null;
      if (workerState) workerState.registration = pendingRegistration;
      if (!registration) return;
      const check = () => {
        registration.update().catch(() => {
          /* offline or transient — next tick will retry */
        });
      };
      schedule(check, UPDATE_CHECK_INTERVAL_MS);
      doc.addEventListener('visibilitychange', () => {
        if (doc.visibilityState === 'visible') check();
      });
    },
    onRegisterError() {
      /* Registration failing must never break the app; the SPA works without a worker. */
    },
  });
  workerState = { updateSW, registration: pendingRegistration };
}
